/**
 * @description Tabbed tenant detail page for admin portal
 *
 * Key features:
 * - Overview tab: Tenant info, status, domain, edit mode, deactivate
 * - Configurations tab: Feature toggles with bulk save + interactive gateway assignment (assign/enable/remove)
 * - Branding tab: Colors and platform name
 * - Merchants tab: shared list chrome (Business / Status / Users / Created).
 *   Hosting is optional column config, LynxPay Sales Org only (no channelPartnerId).
 *   Row opens shared MerchantDrawer for websites Paid/Unpaid (LYN-80)
 *
 * @dependencies
 * - adminApi: getTenant, updateTenant, deactivateTenant, updateTenantFeatures,
 *             updateTenantBranding, getTenantMerchants, getPlatformGateways,
 *             getSOGateways, createSOGateway, updateSOGateway, deactivateSOGateway
 * - MerchantDrawer: shared websites hosting flip (LYN-76 / LYN-80)
 * - AdminListTable + merchantListColumns({ includeHosting })
 * - @headlessui/react: Tab
 * - react-router-dom: useParams, useNavigate
 * - lucide-react: icons
 *
 * @notes
 * - Data shape from GET /api/admin/tenants/:id:
 *   { id, organizationName, contactEmail, contactPhone, wristbandTenantId,
 *     channelPartnerId, channelPartnerName, branding, isActive, createdAt, updatedAt,
 *     companies: [{ id, businessName, createdAt }],
 *     features: [{ id, featureName, isEnabled, enabledAt, disabledAt }],
 *     domain: { slug, customDomainBase, customDomainStatus } | null }
 * - Under-CP click-into (LYN-325) reuses this page at /tenants/:id. Back uses
 *   channelPartnerId. Sidebar keeps Channel Partners selected. Host line stays
 *   getSoOccupancyDoorUrl. Do not hide Branding.
 * - Under-CP Signup capabilities switch (LYN-320) lives on Overview. Hidden
 *   when the partner hid Sales Org signup. Not LynxPay SOs. Not so_features.
 * - Merchants tab fetches separately via GET /api/admin/tenants/:id/merchants
 *   for richer data (userCount, isActive, cmsSiteId, hostingTier per merchant)
 * - Status (accountStatus active|inactive|unlinked) ≠ Hosting Paid — independent;
 *   hosting flip reuses PATCH /api/admin/cms-sites/by-company/:companyId/hosting-tier
 * - FeaturesTab getAdminCompany(soSelfCompanyId) uses a cancelled flag so a
 *   stale response cannot overwrite (or PATCH) the previous SO self-company
 *   when /tenants/:id changes without unmounting this page
 */

import { useState, useEffect, useCallback, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tab, Dialog, Transition } from '@headlessui/react';
import {
  ArrowLeft, AlertCircle, Save, Loader2, Check, X,
  Building2, Palette, ToggleLeft, Users, Globe, Pencil,
  Upload, Trash2, Image, Sun, Moon, Send, Plus
} from 'lucide-react';
import {
  getTenant, updateTenant, deactivateTenant, sendTenantInvite,
  updateTenantSignupCapabilities,
  updateTenantFeatures, updateTenantBranding, getTenantMerchants,
  uploadTenantLogo, uploadTenantFavicon, deleteTenantLogo, deleteTenantFavicon,
  uploadTenantLogoMark, deleteTenantLogoMark,
  getPlatformGateways, getSOGateways, createSOGateway, updateSOGateway, deactivateSOGateway,
  getAdminCompany,
  getTenantSourceConnectors,
  saveTenantSourceConnector
} from '../../services/api/adminApi';
import MerchantAliasEditor from '../../components/merchants/MerchantAliasEditor';
import { getSoOccupancyDoorUrl } from '../../config/domains';
import MerchantDrawer from '../../components/merchants/MerchantDrawer';
import SOCustomDomainPanel from '../../components/tenants/SOCustomDomainPanel';
import AdminListTable from '../../components/admin-list/AdminListTable';
import { merchantListColumns } from '../../components/admin-list/adminListColumns';
import { resolveMerchantCompanyId } from '../../utils/resolveMerchantCompanyId';
import { tenantDetailBackLabel, tenantDetailBackPath } from '../../utils/createSalesOrgUnderCp';
import { useAdminNav } from '../../contexts/AdminNavContext';
import { US_IANA_TIMEZONES } from '../../constants/usIanaTimezones';

// Features that are actually wired up and functional.
// Others (invoicing, payroll, pos_systems, advanced_reporting) exist in the DB
// but no code gates on them yet — hidden from UI to avoid confusion.
const ACTIVE_FEATURES = ['payment_gateways', 'cms'];

// Feature name display mapping
const FEATURE_LABELS = {
  payment_gateways: 'Payment Gateways',
  cms: 'CMS',
};

const DEFAULT_CMS_AUDIENCES = {
  soAccess: false,
  merchantAccess: false,
};

/**
 * Build feature rows for the Configurations tab, including defaults for active features
 * not yet persisted on the tenant.
 * @param {Array} tenantFeatures - Features from GET /api/admin/tenants/:id
 * @returns {Array} Feature rows for rendering
 */
function buildFeatureRows(tenantFeatures = []) {
  const byName = Object.fromEntries(
    tenantFeatures.map(f => [f.featureName, f])
  );

  return ACTIVE_FEATURES.map((featureName) => {
    const existing = byName[featureName];
    if (existing) {
      return existing;
    }
    return {
      featureName,
      isEnabled: false,
      configuration: featureName === 'cms'
        ? { audiences: { so_access: false, merchant_access: false } }
        : {},
    };
  });
}

/**
 * Read CMS audience toggles from API configuration (snake_case) into UI state (camelCase)
 * @param {object} configuration - Feature configuration from API
 * @returns {{ soAccess: boolean, merchantAccess: boolean }}
 */
function readCmsAudiences(configuration = {}) {
  const audiences = configuration.audiences || {};
  return {
    soAccess: Boolean(audiences.so_access),
    merchantAccess: Boolean(audiences.merchant_access),
  };
}

function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setTenantChannelPartnerId } = useAdminNav();

  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTenant = useCallback(async () => {
    try {
      const result = await getTenant(id);
      if (result.success) {
        setTenant(result.data);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load tenant');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  useEffect(() => {
    if (!tenant) {
      setTenantChannelPartnerId(undefined);
      return undefined;
    }
    setTenantChannelPartnerId(tenant.channelPartnerId || null);
    return () => setTenantChannelPartnerId(undefined);
  }, [tenant, setTenantChannelPartnerId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-800 border-t-transparent" />
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] text-center">
        <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-gray-900 font-medium mb-1">Failed to load tenant</p>
        <p className="text-gray-500 text-sm">{error || 'Tenant not found'}</p>
        <button
          onClick={() => navigate('/tenants')}
          className="mt-4 text-sm text-indigo-600 hover:text-indigo-500"
        >
          Back to Tenants
        </button>
      </div>
    );
  }

  const tabs = [
    { name: 'Overview', icon: Building2 },
    { name: 'Configurations', icon: ToggleLeft },
    { name: 'Branding', icon: Palette },
    { name: 'Merchants', icon: Users },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(tenantDetailBackPath(tenant))}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {tenantDetailBackLabel(tenant)}
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-gray-900">{tenant.organizationName}</h1>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
            tenant.isActive
              ? 'bg-green-50 text-green-700'
              : 'bg-gray-100 text-gray-500'
          }`}>
            {tenant.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
        {tenant.domain?.slug && (
          <p className="text-sm text-gray-400 mt-0.5 flex items-center gap-1">
            <Globe className="w-3.5 h-3.5" />
            {getSoOccupancyDoorUrl(tenant.domain)}
          </p>
        )}
      </div>

      {/* Tabs */}
      <Tab.Group>
        <Tab.List className="flex border-b border-gray-200 mb-6">
          {tabs.map((tab) => (
            <Tab key={tab.name} as={Fragment}>
              {({ selected }) => (
                <button
                  className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors focus:outline-none ${
                    selected
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.name}
                </button>
              )}
            </Tab>
          ))}
        </Tab.List>

        <Tab.Panels>
          <Tab.Panel>
            <OverviewTab tenant={tenant} onUpdate={fetchTenant} navigate={navigate} />
          </Tab.Panel>
          <Tab.Panel>
            <FeaturesTab tenant={tenant} onUpdate={fetchTenant} />
          </Tab.Panel>
          <Tab.Panel>
            <BrandingTab tenant={tenant} onUpdate={fetchTenant} />
          </Tab.Panel>
          <Tab.Panel>
            <MerchantsTab tenantId={id} showHosting={!tenant.channelPartnerId} />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────

function SalesOrgSignupSwitch({ tenant, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const enabled = tenant.publicSignupEnabled !== false;

  const handleToggle = async () => {
    setBusy(true);
    setError(null);
    try {
      await updateTenantSignupCapabilities(tenant.id, { publicSignupEnabled: !enabled });
      await onUpdate();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to update signup');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-4 py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">Public signup</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Merchants may sign up on this Sales Org app.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="Public signup"
          disabled={busy}
          onClick={handleToggle}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-50 ${
            enabled ? 'bg-indigo-600' : 'bg-gray-200'
          }`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`} />
        </button>
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function OverviewTab({ tenant, onUpdate, navigate }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [deactivating, setDeactivating] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const [form, setForm] = useState({
    organizationName: tenant.organizationName || '',
    contactEmail: tenant.contactEmail || '',
    contactPhone: tenant.contactPhone || '',
    timezone: tenant.timezone || '',
  });

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await updateTenant(tenant.id, {
        organizationName: form.organizationName.trim(),
        contactEmail: form.contactEmail.trim() || undefined,
        contactPhone: form.contactPhone.trim() || undefined,
        timezone: form.timezone || undefined,
      });
      setEditing(false);
      await onUpdate();
    } catch (err) {
      setSaveError(err.response?.data?.error?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    setDeactivating(true);
    try {
      await deactivateTenant(tenant.id);
      await onUpdate();
      setShowDeactivateConfirm(false);
    } catch (err) {
      setSaveError(err.response?.data?.error?.message || 'Failed to deactivate tenant');
    } finally {
      setDeactivating(false);
    }
  };

  const handleSendInvite = async () => {
    setSendingInvite(true);
    setInviteResult(null);
    try {
      const result = await sendTenantInvite(tenant.id);
      setInviteResult({ type: 'success', message: result.message || 'Invitation sent' });
      await onUpdate();
      setTimeout(() => setInviteResult(null), 5000);
    } catch (err) {
      setInviteResult({ type: 'error', message: err.response?.data?.error?.message || 'Failed to send invite' });
    } finally {
      setSendingInvite(false);
    }
  };

  const handleCancelEdit = () => {
    setForm({
      organizationName: tenant.organizationName || '',
      contactEmail: tenant.contactEmail || '',
      contactPhone: tenant.contactPhone || '',
      timezone: tenant.timezone || '',
    });
    setEditing(false);
    setSaveError(null);
  };

  return (
    <div className="space-y-6">
      {saveError && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{saveError}</span>
        </div>
      )}

      {/* Admin User invite — under sub-nav, above Organization Details. Gate on Lynx pending. */}
      {tenant.signupCapabilityVisible && (
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">Signup capabilities</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Public signup on this Sales Org app is the merchant form. Hidden entirely
              when the Channel Partner turns Sales Org applications off.
            </p>
          </div>
          <SalesOrgSignupSwitch tenant={tenant} onUpdate={onUpdate} />
        </div>
      )}

      {tenant.adminEmail && (
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">Admin User</p>
          </div>
          <div className="px-4 py-3 space-y-3">
            <InfoRow label="Email">
              <span className="text-sm text-gray-900">{tenant.adminEmail}</span>
            </InfoRow>
            <InfoRow label="Status">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                tenant.invitationStatus === 'accepted'
                  ? 'bg-green-50 text-green-700'
                  : tenant.invitationStatus === 'pending'
                    ? 'bg-blue-50 text-blue-700'
                    : 'bg-amber-50 text-amber-700'
              }`}>
                {tenant.invitationStatus === 'accepted' ? 'Accepted' :
                 tenant.invitationStatus === 'pending' ? 'Invite Sent' :
                 tenant.invitationStatus === 'expired' ? 'Invite Expired' :
                 tenant.invitationStatus === 'not_invited' ? 'Awaiting Invite' :
                 tenant.invitationStatus || 'Awaiting Invite'}
              </span>
            </InfoRow>
            {tenant.invitationStatus !== 'accepted' && (
              <div className="pt-1">
                {inviteResult && (
                  <div className={`mb-2 p-2 rounded text-xs flex items-center gap-1.5 ${
                    inviteResult.type === 'success'
                      ? 'bg-green-50 text-green-700'
                      : 'bg-red-50 text-red-700'
                  }`}>
                    {inviteResult.type === 'success' ? <Check className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                    {inviteResult.message}
                  </div>
                )}
                <button
                  onClick={handleSendInvite}
                  disabled={sendingInvite}
                  className="inline-flex items-center gap-1.5 text-sm text-white bg-indigo-600 hover:bg-indigo-500 px-3 py-1.5 rounded-md font-medium disabled:opacity-50 transition-colors"
                >
                  {sendingInvite ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  {tenant.invitationStatus === 'pending' || tenant.invitationStatus === 'expired'
                    ? 'Resend Invite'
                    : 'Send Invite'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-900">Organization Details</p>
          {!editing ? (
            <button
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-500 font-medium"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancelEdit}
                disabled={saving}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 font-medium"
              >
                <X className="w-3.5 h-3.5" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-1 text-xs text-white bg-indigo-600 hover:bg-indigo-500 px-2.5 py-1 rounded-md font-medium disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save
              </button>
            </div>
          )}
        </div>

        <div className="px-4 py-3 space-y-3">
          <InfoRow label="Organization Name" editing={editing}>
            {editing ? (
              <input
                type="text"
                value={form.organizationName}
                onChange={(e) => setForm(prev => ({ ...prev, organizationName: e.target.value }))}
                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
              />
            ) : (
              <span className="text-sm text-gray-900 font-medium">{tenant.organizationName}</span>
            )}
          </InfoRow>

          <InfoRow label="Contact Email" editing={editing}>
            {editing ? (
              <input
                type="email"
                value={form.contactEmail}
                onChange={(e) => setForm(prev => ({ ...prev, contactEmail: e.target.value }))}
                placeholder="Not set"
                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
              />
            ) : (
              <span className="text-sm text-gray-900">{tenant.contactEmail || '—'}</span>
            )}
          </InfoRow>

          <InfoRow label="Contact Phone" editing={editing}>
            {editing ? (
              <input
                type="tel"
                value={form.contactPhone}
                onChange={(e) => setForm(prev => ({ ...prev, contactPhone: e.target.value }))}
                placeholder="Not set"
                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
              />
            ) : (
              <span className="text-sm text-gray-900">{tenant.contactPhone || '—'}</span>
            )}
          </InfoRow>

          <InfoRow label="Timezone" editing={editing}>
            {editing ? (
              <select
                value={form.timezone}
                onChange={(e) => setForm(prev => ({ ...prev, timezone: e.target.value }))}
                className="w-full px-2.5 py-1.5 border border-gray-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
              >
                <option value="">Select timezone</option>
                {US_IANA_TIMEZONES.map((zone) => (
                  <option key={zone.value} value={zone.value}>
                    {zone.label}
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-sm text-gray-900">{tenant.timezone || '—'}</span>
            )}
          </InfoRow>

          {tenant.channelPartnerId && (
            <InfoRow label="Channel Partner">
              <span className="text-sm text-gray-900">
                {tenant.channelPartnerName || tenant.channelPartnerId}
              </span>
            </InfoRow>
          )}
        </div>
      </div>

      {/* System Info (read-only) */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-900">System Information</p>
        </div>
        <div className="px-4 py-3 space-y-3">
          <InfoRow label="Tenant ID">
            <span className="text-sm text-gray-600 font-mono">{tenant.id}</span>
          </InfoRow>
          <InfoRow label="Wristband Tenant ID">
            <span className="text-sm text-gray-600 font-mono">{tenant.wristbandTenantId || '—'}</span>
          </InfoRow>
          {tenant.domain && (
            <>
              <InfoRow label="Slug">
                <span className="text-sm text-gray-900">{tenant.domain.slug}</span>
                <span className="text-xs text-gray-400 ml-2">{getSoOccupancyDoorUrl(tenant.domain)}</span>
              </InfoRow>
              {tenant.domain.customDomainBase && (
                <InfoRow label="Custom Domain">
                  <span className="text-sm text-gray-900">{tenant.domain.customDomainBase}</span>
                  <span className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                    tenant.domain.customDomainStatus === 'verified'
                      ? 'bg-green-50 text-green-700'
                      : tenant.domain.customDomainStatus === 'failed'
                        ? 'bg-red-50 text-red-700'
                        : 'bg-yellow-50 text-yellow-700'
                  }`}>
                    {tenant.domain.customDomainStatus || 'pending'}
                  </span>
                </InfoRow>
              )}
            </>
          )}
          <InfoRow label="Created">
            <span className="text-sm text-gray-600">{new Date(tenant.createdAt).toLocaleDateString()}</span>
          </InfoRow>
          <InfoRow label="Last Updated">
            <span className="text-sm text-gray-600">{new Date(tenant.updatedAt).toLocaleDateString()}</span>
          </InfoRow>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="px-4 py-4">
          <SOCustomDomainPanel
            tenantId={tenant.id}
            slug={tenant.domain?.slug}
            domain={tenant.domain}
            onDomainChange={onUpdate}
          />
        </div>
      </div>

      {/* Deactivate */}
      {tenant.isActive && (
        <div className="bg-white border border-red-200 rounded-xl">
          <div className="px-4 py-3">
            <p className="text-sm font-medium text-red-900">Danger Zone</p>
            <p className="text-xs text-red-600 mt-0.5">Deactivating a tenant disables access for all its merchants</p>
            <div className="mt-3">
              {!showDeactivateConfirm ? (
                <button
                  onClick={() => setShowDeactivateConfirm(true)}
                  className="text-sm text-red-600 hover:text-red-500 font-medium border border-red-200 px-3 py-1.5 rounded-md hover:bg-red-50 transition-colors"
                >
                  Deactivate Tenant
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-red-700">Are you sure?</span>
                  <button
                    onClick={handleDeactivate}
                    disabled={deactivating}
                    className="text-sm text-white bg-red-600 hover:bg-red-500 px-3 py-1.5 rounded-md font-medium disabled:opacity-50"
                  >
                    {deactivating ? 'Deactivating...' : 'Yes, deactivate'}
                  </button>
                  <button
                    onClick={() => setShowDeactivateConfirm(false)}
                    className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Configurations Tab ────────────────────────────────────────

function TenantConnectorsOverride({ tenant }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const res = await getTenantSourceConnectors(tenant.id);
    setRows(res.data || []);
  }, [tenant.id]);

  useEffect(() => {
    load().catch((err) => {
      setError(err.response?.data?.error?.message || 'Failed to load connectors');
    });
  }, [load]);

  return (
    <section className="bg-white border border-gray-200 rounded-xl mb-6" data-testid="so-connector-override">
      <div className="px-5 py-3 border-b border-gray-100">
        <h3 className="text-sm font-medium text-gray-900">Connectors</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Rare SO override. Last save wins. Login verify has no scheduler.
        </p>
      </div>
      {error ? <p className="px-5 py-2 text-xs text-red-600">{error}</p> : null}
      <ul className="px-5 py-3 space-y-3">
        {rows.map((row) => (
          <li key={row.id} className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-gray-900">{row.title}</p>
              <p className="text-xs text-gray-500">{row.description}</p>
            </div>
            <button
              type="button"
              disabled={busy || (!row.flag?.flagged && row.canFlag === false)}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  await saveTenantSourceConnector(tenant.id, row.id, {
                    flagged: !(row.flag?.flagged === true)
                  });
                  await load();
                } catch (err) {
                  setError(err.response?.data?.error?.message || 'Failed to save override');
                } finally {
                  setBusy(false);
                }
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                row.flag?.flagged ? 'bg-indigo-600' : 'bg-gray-200'
              } disabled:opacity-50`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white ${
                row.flag?.flagged ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function FeaturesTab({ tenant, onUpdate }) {
  const [features, setFeatures] = useState({});
  const [cmsAudiences, setCmsAudiences] = useState(DEFAULT_CMS_AUDIENCES);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);

  const featureRows = buildFeatureRows(tenant.features || []);

  // Platform gateways (for assignment modal options)
  const [platformGateways, setPlatformGateways] = useState([]);
  // SO gateways (assigned to this tenant)
  const [soGateways, setSOGateways] = useState([]);
  const [gatewaysLoading, setGatewaysLoading] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigning, setAssigning] = useState(null); // platform gateway ID being assigned
  const [removingId, setRemovingId] = useState(null); // SO gateway ID pending removal confirm
  const [gatewayError, setGatewayError] = useState(null);
  const [soSelfCompany, setSoSelfCompany] = useState(null);

  // Initialize features from tenant data
  useEffect(() => {
    const rows = buildFeatureRows(tenant.features || []);
    const featureMap = {};
    rows.forEach(f => {
      featureMap[f.featureName] = f.isEnabled;
    });
    setFeatures(featureMap);

    const cmsFeature = rows.find(f => f.featureName === 'cms');
    setCmsAudiences(readCmsAudiences(cmsFeature?.configuration));
  }, [tenant.features]);

  // Load platform gateways + SO gateways when payment_gateways is enabled
  const loadGateways = useCallback(async () => {
    setGatewaysLoading(true);
    setGatewayError(null);
    try {
      const [platformResult, soResult] = await Promise.all([
        getPlatformGateways(),
        getSOGateways(tenant.id)
      ]);
      setPlatformGateways((platformResult.data || []).filter(gw => gw.isEnabled));
      setSOGateways(soResult.data || []);
    } catch {
      setGatewayError('Failed to load gateways');
    } finally {
      setGatewaysLoading(false);
    }
  }, [tenant.id]);

  useEffect(() => {
    if (features.payment_gateways) {
      loadGateways();
    }
  }, [features.payment_gateways, loadGateways]);

  useEffect(() => {
    const selfId = tenant.soSelfCompanyId;
    if (!selfId) {
      setSoSelfCompany(null);
      return undefined;
    }

    let cancelled = false;
    setSoSelfCompany((prev) => (prev?.id === selfId ? prev : null));

    getAdminCompany(selfId)
      .then((result) => {
        if (cancelled) return;
        if (result.success) setSoSelfCompany(result.data);
      })
      .catch(() => {
        if (!cancelled) setSoSelfCompany(null);
      });

    return () => {
      cancelled = true;
    };
  }, [tenant.soSelfCompanyId]);

  const hasChanges = () => {
    const featureChanged = featureRows.some(f => features[f.featureName] !== f.isEnabled);
    const cmsFeature = featureRows.find(f => f.featureName === 'cms');
    const savedAudiences = readCmsAudiences(cmsFeature?.configuration);
    const audiencesChanged =
      cmsAudiences.soAccess !== savedAudiences.soAccess ||
      cmsAudiences.merchantAccess !== savedAudiences.merchantAccess;
    return featureChanged || audiencesChanged;
  };

  const buildFeaturesPayload = () => {
    const payload = { ...features };

    payload.cms = {
      isEnabled: Boolean(features.cms),
      configuration: {
        audiences: {
          so_access: cmsAudiences.soAccess,
          merchant_access: cmsAudiences.merchantAccess,
        },
      },
    };

    return payload;
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      await updateTenantFeatures(tenant.id, buildFeaturesPayload());
      setSaveResult('success');
      await onUpdate();
      setTimeout(() => setSaveResult(null), 2000);
    } catch (err) {
      setSaveResult(err.response?.data?.error?.message || 'Failed to update features');
    } finally {
      setSaving(false);
    }
  };

  // Assign a platform gateway to this SO
  const handleAssign = async (platformGw) => {
    setAssigning(platformGw.id);
    setGatewayError(null);
    try {
      await createSOGateway({
        salesOrgId: tenant.id,
        platformGatewayId: platformGw.id,
        gatewayName: platformGw.gatewayName,
        gatewayType: platformGw.gatewayType
      });
      setAssignModalOpen(false);
      await loadGateways();
    } catch (err) {
      const msg = err.response?.data?.error?.message || 'Failed to assign gateway';
      setGatewayError(msg);
    } finally {
      setAssigning(null);
    }
  };

  // Toggle SO gateway enabled/disabled
  const handleToggleEnabled = async (soGw) => {
    const prev = soGw.isEnabled;
    // Optimistic update
    setSOGateways(gws => gws.map(g => g.id === soGw.id ? { ...g, isEnabled: !prev } : g));
    try {
      await updateSOGateway(soGw.id, { isEnabled: !prev });
    } catch {
      // Revert on failure
      setSOGateways(gws => gws.map(g => g.id === soGw.id ? { ...g, isEnabled: prev } : g));
      setGatewayError('Failed to update gateway status');
    }
  };

  // Deactivate (remove) SO gateway
  const handleRemove = async (soGwId) => {
    setGatewayError(null);
    try {
      await deactivateSOGateway(soGwId);
      setSOGateways(gws => gws.filter(g => g.id !== soGwId));
      setRemovingId(null);
    } catch {
      setGatewayError('Failed to remove gateway');
    }
  };

  // Platform gateways not yet assigned to this SO
  const assignedPlatformIds = new Set(soGateways.map(g => g.platformGatewayId));
  const unassignedPlatformGateways = platformGateways.filter(pg => !assignedPlatformIds.has(pg.id));

  return (
    <div>
      {tenant.channelPartnerId ? (
        <TenantConnectorsOverride tenant={tenant} />
      ) : null}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium text-gray-900">Tenant Configurations</h3>
          <p className="text-xs text-gray-500 mt-0.5">Toggle features and configure integrations for this tenant</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges()}
          className="inline-flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-indigo-500 disabled:opacity-50 transition-colors"
        >
          {saving ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : saveResult === 'success' ? (
            <Check className="w-3.5 h-3.5" />
          ) : (
            <Save className="w-3.5 h-3.5" />
          )}
          {saving ? 'Saving...' : saveResult === 'success' ? 'Saved' : 'Save Changes'}
        </button>
      </div>

      {typeof saveResult === 'string' && saveResult !== 'success' && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {saveResult}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
        {featureRows.map((feature) => (
          <div key={feature.featureName}>
            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {FEATURE_LABELS[feature.featureName] || feature.featureName}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {feature.isEnabled && feature.enabledAt
                    ? `Enabled ${new Date(feature.enabledAt).toLocaleDateString()}`
                    : feature.isEnabled
                      ? 'Enabled'
                      : feature.disabledAt
                        ? `Disabled ${new Date(feature.disabledAt).toLocaleDateString()}`
                        : 'Not configured'}
                </p>
              </div>
              <button
                onClick={() => setFeatures(prev => ({
                  ...prev,
                  [feature.featureName]: !prev[feature.featureName]
                }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${
                  features[feature.featureName] ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
                role="switch"
                aria-checked={features[feature.featureName]}
                aria-label={`Toggle ${FEATURE_LABELS[feature.featureName] || feature.featureName}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                  features[feature.featureName] ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            {/* CMS audience panel — shown when CMS master toggle is ON */}
            {feature.featureName === 'cms' && features.cms && (
              <div className="px-4 pb-3 pt-2 bg-gray-50 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-700 mb-2">Audience Access</p>
                <div className="space-y-2">
                  {[
                    {
                      key: 'soAccess',
                      label: 'SO Access',
                      description: 'Allow SO portal users to access Lynx Websites',
                    },
                    {
                      key: 'merchantAccess',
                      label: 'Merchant Access',
                      description: 'Allow merchants under this tenant to access Lynx Websites',
                    },
                  ].map(({ key, label, description }) => (
                    <div
                      key={key}
                      className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">{label}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                      </div>
                      <button
                        onClick={() => setCmsAudiences(prev => ({
                          ...prev,
                          [key]: !prev[key],
                        }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${
                          cmsAudiences[key] ? 'bg-indigo-600' : 'bg-gray-200'
                        }`}
                        role="switch"
                        aria-checked={cmsAudiences[key]}
                        aria-label={`Toggle ${label}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                          cmsAudiences[key] ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Gateway assignment panel — shown when payment_gateways is enabled */}
            {feature.featureName === 'payment_gateways' && features.payment_gateways && (
              <div className="px-4 pb-3 pt-2 bg-gray-50 border-t border-gray-100">
                {/* Header with assign button */}
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-gray-700">
                    Assigned Gateways
                  </label>
                  <button
                    onClick={() => { setGatewayError(null); setAssignModalOpen(true); }}
                    disabled={gatewaysLoading}
                    className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 bg-white border border-gray-300 rounded-md px-2 py-1 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                    Assign Gateway
                  </button>
                </div>

                {/* Error banner */}
                {gatewayError && (
                  <div className="mb-2 p-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-700 flex items-center justify-between">
                    <span>{gatewayError}</span>
                    <button onClick={() => setGatewayError(null)} className="text-red-400 hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {/* Loading state */}
                {gatewaysLoading ? (
                  <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Loading gateways...
                  </div>
                ) : soGateways.length === 0 ? (
                  /* Empty state */
                  <div className="py-3 text-center">
                    <p className="text-xs text-gray-500">
                      No gateways assigned yet.
                      {platformGateways.length === 0
                        ? <> Configure one in Integrations &rarr; Gateways first.</>
                        : <> Click &ldquo;Assign Gateway&rdquo; to get started.</>
                      }
                    </p>
                  </div>
                ) : (
                  /* Assigned gateway cards */
                  <div className="space-y-1.5">
                    {soGateways.map(gw => (
                      <div
                        key={gw.id}
                        className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-2"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-medium text-gray-900 truncate">{gw.gatewayName}</span>
                          {gw.gatewayType !== gw.gatewayName.toLowerCase() && (
                            <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 rounded flex-shrink-0">
                              {gw.gatewayType}
                            </span>
                          )}
                          {gw.platformGatewayName && gw.platformGatewayName !== gw.gatewayName && (
                            <span className="text-xs text-gray-400 truncate flex-shrink-0">
                              from {gw.platformGatewayName}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          {/* Enable/disable toggle */}
                          <button
                            onClick={() => handleToggleEnabled(gw)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${
                              gw.isEnabled ? 'bg-indigo-600' : 'bg-gray-200'
                            }`}
                            role="switch"
                            aria-checked={gw.isEnabled}
                            aria-label={`${gw.isEnabled ? 'Disable' : 'Enable'} ${gw.gatewayName}`}
                            title={gw.isEnabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                              gw.isEnabled ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </button>

                          {/* Remove button with inline confirm */}
                          {removingId === gw.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleRemove(gw.id)}
                                className="text-xs text-white bg-red-600 hover:bg-red-500 px-2 py-0.5 rounded font-medium"
                              >
                                Remove
                              </button>
                              <button
                                onClick={() => setRemovingId(null)}
                                className="text-xs text-gray-500 hover:text-gray-700 px-1"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setRemovingId(gw.id)}
                              className="text-gray-400 hover:text-red-500 transition-colors p-0.5"
                              title="Remove gateway"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {soGateways.some((gw) => gw.gatewayType === 'elavon' && gw.isEnabled) && soSelfCompany && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <MerchantAliasEditor
                      companyId={soSelfCompany.id}
                      elavonAssigned
                      initialAlias={soSelfCompany.merchantAlias || ''}
                      onSaved={(data) => setSoSelfCompany((prev) => ({ ...prev, ...data }))}
                    />
                  </div>
                )}

                {/* Assign Gateway Modal */}
                <Transition show={assignModalOpen} as={Fragment}>
                  <Dialog onClose={() => setAssignModalOpen(false)} className="relative z-50">
                    <Transition.Child
                      as={Fragment}
                      enter="ease-out duration-200"
                      enterFrom="opacity-0"
                      enterTo="opacity-100"
                      leave="ease-in duration-150"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                    </Transition.Child>

                    <div className="fixed inset-0 flex items-center justify-center p-4">
                      <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                      >
                        <Dialog.Panel className="bg-white rounded-xl shadow-xl max-w-md w-full p-5">
                          <Dialog.Title className="text-sm font-semibold text-gray-900 mb-1">
                            Assign Platform Gateway
                          </Dialog.Title>
                          <p className="text-xs text-gray-500 mb-4">
                            Select a platform gateway to assign to this tenant.
                          </p>

                          {/* Error in modal */}
                          {gatewayError && (
                            <div className="mb-3 p-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-700">
                              {gatewayError}
                            </div>
                          )}

                          {unassignedPlatformGateways.length === 0 ? (
                            <div className="py-6 text-center">
                              <p className="text-xs text-gray-500">
                                {platformGateways.length === 0
                                  ? 'No enabled platform gateways available. Configure one in Integrations \u2192 Gateways first.'
                                  : 'All platform gateways are already assigned to this tenant.'}
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {unassignedPlatformGateways.map(pg => (
                                <div
                                  key={pg.id}
                                  className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2.5"
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-sm font-medium text-gray-900 truncate">{pg.gatewayName}</span>
                                    {pg.gatewayType !== pg.gatewayName.toLowerCase() && (
                                      <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 rounded flex-shrink-0">
                                        {pg.gatewayType}
                                      </span>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => handleAssign(pg)}
                                    disabled={assigning === pg.id}
                                    className="inline-flex items-center gap-1 text-xs font-medium text-white bg-slate-800 rounded-md px-2.5 py-1 hover:bg-slate-700 disabled:opacity-50 transition-colors flex-shrink-0 ml-2"
                                  >
                                    {assigning === pg.id ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Plus className="w-3 h-3" />
                                    )}
                                    Assign
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="mt-4 flex justify-end">
                            <button
                              onClick={() => setAssignModalOpen(false)}
                              className="text-xs font-medium text-gray-600 hover:text-gray-800 px-3 py-1.5"
                            >
                              Close
                            </button>
                          </div>
                        </Dialog.Panel>
                      </Transition.Child>
                    </div>
                  </Dialog>
                </Transition>
              </div>
            )}
          </div>
        ))}

        {(!tenant.features || tenant.features.length === 0) && (
          <div className="px-4 py-8 text-center">
            <ToggleLeft className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No features configured for this tenant</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Branding Tab ──────────────────────────────────────────────

function BrandingTab({ tenant, onUpdate }) {
  const existing = tenant.branding || {};

  const [identity, setIdentity] = useState({
    platformName: existing.identity?.platformName || '',
    documentTitle: existing.identity?.documentTitle || '',
    tagline: existing.identity?.tagline || '',
  });

  const [colors, setColors] = useState({
    mode: existing.colors?.mode || 'dark',
    primaryColor: existing.colors?.primaryColor || '#4F46E5',
    secondaryColor: existing.colors?.secondaryColor || '#6366F1',
  });

  const [meta, setMeta] = useState({
    themeColor: existing.meta?.themeColor || '#0a0a0b',
    description: existing.meta?.description || '',
  });

  // Reset identity/colors/meta when navigating between tenants (Tab may remount,
  // but reset explicitly so a shared parent session cannot leak form state).
  useEffect(() => {
    const next = tenant.branding || {};
    setIdentity({
      platformName: next.identity?.platformName || '',
      documentTitle: next.identity?.documentTitle || '',
      tagline: next.identity?.tagline || '',
    });
    setColors({
      mode: next.colors?.mode || 'dark',
      primaryColor: next.colors?.primaryColor || '#4F46E5',
      secondaryColor: next.colors?.secondaryColor || '#6366F1',
    });
    setMeta({
      themeColor: next.meta?.themeColor || '#0a0a0b',
      description: next.meta?.description || '',
    });
  }, [tenant.id]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: reset on tenant switch only

  // Asset URLs are read from tenant.branding — uploads are handled separately
  const logoUrl = existing.assets?.logoUrl || null;
  const logoMarkUrl = existing.assets?.logoMarkUrl || null;
  const faviconUrl = existing.assets?.faviconUrl || null;

  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [uploading, setUploading] = useState(null); // 'logo' | 'logoMark' | 'favicon' | null
  const [uploadError, setUploadError] = useState(null);

  // Block all asset controls while any upload/delete is in flight
  const assetsBusy = uploading !== null;

  const handleSave = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      // Omit documentTitle and tagline if empty — they're optional and the
      // validator rejects present-but-empty strings for documentTitle
      const cleanIdentity = { platformName: identity.platformName };
      if (identity.documentTitle.trim()) cleanIdentity.documentTitle = identity.documentTitle;
      if (identity.tagline.trim()) cleanIdentity.tagline = identity.tagline;
      await updateTenantBranding(tenant.id, { identity: cleanIdentity, colors, meta });
      setSaveResult('success');
      await onUpdate();
      setTimeout(() => setSaveResult(null), 2000);
    } catch (err) {
      setSaveResult(err.response?.data?.error?.message || 'Failed to update branding');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading('logo');
    setUploadError(null);
    try {
      await uploadTenantLogo(tenant.id, file);
      await onUpdate();
    } catch (err) {
      setUploadError(err.response?.data?.error?.message || 'Failed to upload logo');
    } finally {
      setUploading(null);
      e.target.value = '';
    }
  };

  const handleLogoMarkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading('logoMark');
    setUploadError(null);
    try {
      await uploadTenantLogoMark(tenant.id, file);
      await onUpdate();
    } catch (err) {
      setUploadError(err.response?.data?.error?.message || 'Failed to upload app icon');
    } finally {
      setUploading(null);
      e.target.value = '';
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading('favicon');
    setUploadError(null);
    try {
      await uploadTenantFavicon(tenant.id, file);
      await onUpdate();
    } catch (err) {
      setUploadError(err.response?.data?.error?.message || 'Failed to upload favicon');
    } finally {
      setUploading(null);
      e.target.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    setUploading('logo');
    setUploadError(null);
    try {
      await deleteTenantLogo(tenant.id);
      await onUpdate();
    } catch (err) {
      setUploadError(err.response?.data?.error?.message || 'Failed to delete logo');
    } finally {
      setUploading(null);
    }
  };

  const handleDeleteLogoMark = async () => {
    setUploading('logoMark');
    setUploadError(null);
    try {
      await deleteTenantLogoMark(tenant.id);
      await onUpdate();
    } catch (err) {
      setUploadError(err.response?.data?.error?.message || 'Failed to delete app icon');
    } finally {
      setUploading(null);
    }
  };

  const handleDeleteFavicon = async () => {
    setUploading('favicon');
    setUploadError(null);
    try {
      await deleteTenantFavicon(tenant.id);
      await onUpdate();
    } catch (err) {
      setUploadError(err.response?.data?.error?.message || 'Failed to delete favicon');
    } finally {
      setUploading(null);
    }
  };

  const isIdentityValid = identity.platformName.trim().length > 0;

  const displayName = identity.platformName || tenant.organizationName || 'Tenant';

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium text-gray-900">Branding Configuration</h3>
          <p className="text-xs text-gray-500 mt-0.5">Customize the appearance for this tenant's portal</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !isIdentityValid}
          className="inline-flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-indigo-500 disabled:opacity-50 transition-colors"
        >
          {saving ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : saveResult === 'success' ? (
            <Check className="w-3.5 h-3.5" />
          ) : (
            <Save className="w-3.5 h-3.5" />
          )}
          {saving ? 'Saving...' : saveResult === 'success' ? 'Saved' : 'Save Changes'}
        </button>
      </div>

      {typeof saveResult === 'string' && saveResult !== 'success' && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{saveResult}</span>
        </div>
      )}

      {uploadError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{uploadError}</span>
        </div>
      )}

      <div className="space-y-6">
        {/* Identity */}
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">Identity</p>
          </div>
          <div className="px-4 py-3 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Platform Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={identity.platformName}
                onChange={(e) => setIdentity(prev => ({ ...prev, platformName: e.target.value }))}
                placeholder={tenant.organizationName}
                className={`w-full max-w-sm px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 ${
                  identity.platformName.trim().length === 0 ? 'border-red-300' : 'border-gray-200'
                }`}
              />
              {identity.platformName.trim().length === 0 ? (
                <p className="text-xs text-red-500 mt-1">Platform name is required</p>
              ) : (
                <p className="text-xs text-gray-400 mt-1">Displayed in the portal header and sidebar</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Document Title</label>
              <input
                type="text"
                value={identity.documentTitle}
                onChange={(e) => setIdentity(prev => ({ ...prev, documentTitle: e.target.value }))}
                placeholder={identity.platformName || tenant.organizationName}
                className="w-full max-w-sm px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
              />
              <p className="text-xs text-gray-400 mt-1">Browser tab title. Falls back to platform name if empty.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
              <input
                type="text"
                value={identity.tagline}
                onChange={(e) => setIdentity(prev => ({ ...prev, tagline: e.target.value }))}
                placeholder="e.g. Automated Payment Solutions"
                className="w-full max-w-sm px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
              />
              <p className="text-xs text-gray-400 mt-1">Shown in the footer and auth pages</p>
            </div>
          </div>
        </div>

        {/* Colors */}
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">Colors</p>
          </div>
          <div className="px-4 py-3 space-y-4">
            <div className="grid grid-cols-2 gap-4 max-w-sm">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={colors.primaryColor}
                    onChange={(e) => setColors(prev => ({ ...prev, primaryColor: e.target.value }))}
                    className="w-8 h-8 rounded border border-gray-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={colors.primaryColor}
                    onChange={(e) => setColors(prev => ({ ...prev, primaryColor: e.target.value }))}
                    className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={colors.secondaryColor}
                    onChange={(e) => setColors(prev => ({ ...prev, secondaryColor: e.target.value }))}
                    className="w-8 h-8 rounded border border-gray-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={colors.secondaryColor}
                    onChange={(e) => setColors(prev => ({ ...prev, secondaryColor: e.target.value }))}
                    className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Mode</label>
              <div className="flex items-center gap-2 max-w-xs">
                <button
                  onClick={() => setColors(prev => ({ ...prev, mode: 'dark' }))}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    colors.mode === 'dark'
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Moon className="w-3.5 h-3.5" />
                  Dark
                </button>
                <button
                  onClick={() => setColors(prev => ({ ...prev, mode: 'light' }))}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    colors.mode === 'light'
                      ? 'bg-white text-gray-900 border-gray-900 ring-1 ring-gray-900'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Sun className="w-3.5 h-3.5" />
                  Light
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Default theme for merchants. Users can override in settings.</p>
            </div>
          </div>
        </div>

        {/* Assets */}
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">Assets</p>
          </div>
          <div className="px-4 py-3 space-y-5">
            {/* Logo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
              <p className="text-xs text-gray-400 mb-2">Wordmark in the app and emails. Also used as the preview image when this link is shared. PNG or JPEG recommended for share cards.</p>
              {logoUrl ? (
                <div className="flex items-center gap-4">
                  <div className="w-32 h-16 border border-gray-200 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden p-2">
                    <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className={`inline-flex items-center gap-1.5 text-xs font-medium ${assetsBusy ? 'text-gray-400 cursor-not-allowed' : 'text-indigo-600 hover:text-indigo-500 cursor-pointer'}`}>
                      <Upload className="w-3.5 h-3.5" />
                      Replace
                      <input type="file" accept="image/svg+xml,image/png,image/jpeg" onChange={handleLogoUpload} disabled={assetsBusy} className="hidden" />
                    </label>
                    <button
                      onClick={handleDeleteLogo}
                      disabled={assetsBusy}
                      className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-500 font-medium disabled:opacity-50"
                    >
                      {uploading === 'logo' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <label className={`flex items-center gap-3 p-4 border-2 border-dashed border-gray-200 rounded-lg transition-colors max-w-sm ${assetsBusy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-300 hover:bg-gray-50'}`}>
                  {uploading === 'logo' ? (
                    <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                  ) : (
                    <Image className="w-6 h-6 text-gray-400" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-700">Upload logo</p>
                    <p className="text-xs text-gray-400">SVG, PNG, or JPEG up to 2MB. PNG/JPEG is used when the link is shared.</p>
                  </div>
                  <input type="file" accept="image/svg+xml,image/png,image/jpeg" onChange={handleLogoUpload} disabled={assetsBusy} className="hidden" />
                </label>
              )}
            </div>

            {/* App Icon */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">App Icon</label>
              <p className="text-xs text-gray-400 mb-2">Square symbol for the collapsed sidebar and the icon when this site is saved to a phone home screen. Use a PNG (not SVG) so iOS/Android can display it.</p>
              {logoMarkUrl ? (
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 border border-gray-200 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden p-1.5">
                    <img src={logoMarkUrl} alt="App Icon" className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className={`inline-flex items-center gap-1.5 text-xs font-medium ${assetsBusy ? 'text-gray-400 cursor-not-allowed' : 'text-indigo-600 hover:text-indigo-500 cursor-pointer'}`}>
                      <Upload className="w-3.5 h-3.5" />
                      Replace
                      <input type="file" accept="image/svg+xml,image/png,image/jpeg" onChange={handleLogoMarkUpload} disabled={assetsBusy} className="hidden" />
                    </label>
                    <button
                      onClick={handleDeleteLogoMark}
                      disabled={assetsBusy}
                      className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-500 font-medium disabled:opacity-50"
                    >
                      {uploading === 'logoMark' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <label className={`flex items-center gap-3 p-4 border-2 border-dashed border-gray-200 rounded-lg transition-colors max-w-xs ${assetsBusy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-300 hover:bg-gray-50'}`}>
                  {uploading === 'logoMark' ? (
                    <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                  ) : (
                    <Image className="w-6 h-6 text-gray-400" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-700">Upload app icon</p>
                    <p className="text-xs text-gray-400">PNG recommended, up to 500KB. SVG will not show on a phone home screen.</p>
                  </div>
                  <input type="file" accept="image/svg+xml,image/png,image/jpeg" onChange={handleLogoMarkUpload} disabled={assetsBusy} className="hidden" />
                </label>
              )}
            </div>

            {/* Favicon */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Favicon</label>
              <p className="text-xs text-gray-400 mb-2">Tiny icon in the browser tab only. ICO/SVG work here; phones ignore them for the home-screen icon.</p>
              {faviconUrl ? (
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 border border-gray-200 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden p-1.5">
                    <img src={faviconUrl} alt="Favicon" className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className={`inline-flex items-center gap-1.5 text-xs font-medium ${assetsBusy ? 'text-gray-400 cursor-not-allowed' : 'text-indigo-600 hover:text-indigo-500 cursor-pointer'}`}>
                      <Upload className="w-3.5 h-3.5" />
                      Replace
                      <input type="file" accept="image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml" onChange={handleFaviconUpload} disabled={assetsBusy} className="hidden" />
                    </label>
                    <button
                      onClick={handleDeleteFavicon}
                      disabled={assetsBusy}
                      className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-500 font-medium disabled:opacity-50"
                    >
                      {uploading === 'favicon' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <label className={`flex items-center gap-3 p-4 border-2 border-dashed border-gray-200 rounded-lg transition-colors max-w-xs ${assetsBusy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-300 hover:bg-gray-50'}`}>
                  {uploading === 'favicon' ? (
                    <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                  ) : (
                    <Image className="w-6 h-6 text-gray-400" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-700">Upload favicon</p>
                    <p className="text-xs text-gray-400">ICO, PNG, or SVG up to 100KB</p>
                  </div>
                  <input type="file" accept="image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml" onChange={handleFaviconUpload} disabled={assetsBusy} className="hidden" />
                </label>
              )}
            </div>
          </div>
        </div>

        {/* Meta */}
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">Meta</p>
          </div>
          <div className="px-4 py-3 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Theme Color</label>
              <div className="flex items-center gap-2 max-w-xs">
                <input
                  type="color"
                  value={meta.themeColor}
                  onChange={(e) => setMeta(prev => ({ ...prev, themeColor: e.target.value }))}
                  className="w-8 h-8 rounded border border-gray-200 cursor-pointer"
                />
                <input
                  type="text"
                  value={meta.themeColor}
                  onChange={(e) => setMeta(prev => ({ ...prev, themeColor: e.target.value }))}
                  className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Browser address bar color on mobile</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={meta.description}
                onChange={(e) => setMeta(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g. Unified merchant management portal"
                className="w-full max-w-sm px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
              />
              <p className="text-xs text-gray-400 mt-1">SEO description for search engines</p>
            </div>
          </div>
        </div>

        {/* Live Preview */}
        <div className="bg-white border border-gray-200 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">Preview</p>
          </div>
          <div className="px-4 py-4">
            {/* Header mockup */}
            <div
              className="rounded-t-lg px-4 py-3 flex items-center justify-between"
              style={{ backgroundColor: colors.mode === 'dark' ? '#0f172a' : '#f8fafc' }}
            >
              <div className="flex items-center gap-2.5">
                {logoUrl ? (
                  <img src={logoUrl} alt="" className="h-7 w-auto" />
                ) : (
                  <div
                    className="w-7 h-7 rounded flex items-center justify-center text-white text-xs font-bold"
                    style={{ backgroundColor: colors.primaryColor }}
                  >
                    {displayName.charAt(0)}
                  </div>
                )}
                <span
                  className="text-sm font-semibold"
                  style={{ color: colors.mode === 'dark' ? '#f8fafc' : '#0f172a' }}
                >
                  {displayName}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="w-6 h-6 rounded-full"
                  style={{ backgroundColor: colors.mode === 'dark' ? '#1e293b' : '#e2e8f0' }}
                />
              </div>
            </div>

            {/* Body mockup */}
            <div
              className="px-4 py-4 rounded-b-lg border-t"
              style={{
                backgroundColor: colors.mode === 'dark' ? '#1e293b' : '#ffffff',
                borderColor: colors.mode === 'dark' ? '#334155' : '#e2e8f0',
              }}
            >
              <div className="flex items-center gap-3 mb-3">
                <button
                  className="px-3 py-1.5 rounded-md text-xs font-medium text-white"
                  style={{ backgroundColor: colors.primaryColor }}
                >
                  Primary Button
                </button>
                <button
                  className="px-3 py-1.5 rounded-md text-xs font-medium text-white"
                  style={{ backgroundColor: colors.secondaryColor }}
                >
                  Secondary
                </button>
              </div>
              <p className="text-xs" style={{ color: colors.mode === 'dark' ? '#94a3b8' : '#64748b' }}>
                {identity.tagline || 'Tagline appears here'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Merchants Tab ──────────────────────────────────────────────

function MerchantsTab({ tenantId, showHosting = true }) {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const fetchMerchants = async () => {
      try {
        const result = await getTenantMerchants(tenantId);
        if (result.success) {
          setMerchants(result.data);
        }
      } catch (err) {
        setError(err.response?.data?.error?.message || 'Failed to load merchants');
      } finally {
        setLoading(false);
      }
    };
    fetchMerchants();
  }, [tenantId]);

  const handleMerchantUpdated = (updated) => {
    setMerchants((prev) =>
      prev.map((m) => {
        const key = resolveMerchantCompanyId(m);
        if (key !== updated.companyId) return m;
        return {
          ...m,
          companyId: updated.companyId || resolveMerchantCompanyId(m),
          cmsSiteId: updated.cmsSiteId ?? m.cmsSiteId,
          hostingTier: updated.hostingTier,
          slug: updated.slug ?? m.slug,
          status: updated.status ?? m.status,
          defaultHostname: updated.defaultHostname ?? m.defaultHostname,
          defaultBaseDomain: updated.defaultBaseDomain ?? m.defaultBaseDomain,
        };
      })
    );
    setSelected((prev) => {
      if (!prev) return prev;
      const key = resolveMerchantCompanyId(prev);
      if (key !== updated.companyId) return prev;
      return {
        ...prev,
        companyId: updated.companyId || resolveMerchantCompanyId(prev),
        cmsSiteId: updated.cmsSiteId ?? prev.cmsSiteId,
        hostingTier: updated.hostingTier,
        slug: updated.slug ?? prev.slug,
        status: updated.status ?? prev.status,
        defaultHostname: updated.defaultHostname ?? prev.defaultHostname,
        defaultBaseDomain: updated.defaultBaseDomain ?? prev.defaultBaseDomain,
      };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-800 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
        <p className="text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  if (merchants.length === 0) {
    return (
      <AdminListTable
        columns={merchantListColumns({ includeHosting: showHosting })}
        rows={[]}
        rowKey={(merchant) => resolveMerchantCompanyId(merchant)}
        emptyIcon={Users}
        emptyMessage="No merchants under this tenant yet"
      />
    );
  }

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-900">Merchants</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          {merchants.length} merchant{merchants.length !== 1 ? 's' : ''} under this tenant
          {' '}· click a row to flip websites Paid/Unpaid
        </p>
      </div>

      <AdminListTable
        columns={merchantListColumns({ includeHosting: showHosting })}
        rows={merchants}
        rowKey={(merchant) => resolveMerchantCompanyId(merchant)}
        onRowClick={(merchant) =>
          setSelected({
            ...merchant,
            companyId: resolveMerchantCompanyId(merchant)
          })
        }
      />

      <MerchantDrawer
        isOpen={Boolean(selected)}
        onClose={() => setSelected(null)}
        merchant={selected}
        onMerchantUpdated={handleMerchantUpdated}
      />
    </div>
  );
}

// ── Shared Components ──────────────────────────────────────────

function InfoRow({ label, children }) {
  return (
    <div className="flex items-start gap-4">
      <span className="text-xs text-gray-500 w-32 pt-1 flex-shrink-0">{label}</span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

export default TenantDetail;
